import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import SavedFilters from '../../../components/filtering/SavedFilters';
import SearchHistory from '../../../components/filtering/SearchHistory';
import BulkActions from '../../../components/filtering/BulkActions';
import { parseNaturalLanguageQuery, generateSearchSuggestions } from '../../../services/naturalLanguageSearchService';

const FilterBar = ({ 
  filters, 
  onFilterChange, 
  onSearch,
  searchHistory = [],
  savedFilters = [],
  onSaveFilter,
  onApplyFilter,
  onDeleteFilter,
  onClearHistory,
  selectedCount = 0,
  onBulkMessage,
  onBulkReveal,
  onBulkArchive,
  onClearSelection
}) => {
  const [naturalLanguageQuery, setNaturalLanguageQuery] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [error, setError] = useState(null);
  const [useNaturalLanguage, setUseNaturalLanguage] = useState(true);

  // Generate suggestions as user types
  useEffect(() => {
    const timer = setTimeout(async () => {
      if (naturalLanguageQuery?.trim()?.length >= 3 && useNaturalLanguage) {
        try {
          const newSuggestions = await generateSearchSuggestions(naturalLanguageQuery);
          setSuggestions(newSuggestions);
          setShowSuggestions(true);
        } catch (err) {
          // Silently fail for suggestions
          setSuggestions([]);
        }
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [naturalLanguageQuery, useNaturalLanguage]);

  const handleNaturalLanguageSearch = async (query) => {
    const searchQuery = query || naturalLanguageQuery;
    if (!searchQuery?.trim()) return;

    setIsProcessing(true);
    setError(null);
    setShowSuggestions(false);

    try {
      const parsedFilters = await parseNaturalLanguageQuery(searchQuery);
      
      // Apply all filters at once
      Object.keys(parsedFilters)?.forEach(key => {
        onFilterChange(key, parsedFilters?.[key]);
      });

      // Track in search history
      if (onSearch) {
        onSearch(searchQuery);
      }
    } catch (err) {
      setError(err?.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setNaturalLanguageQuery(suggestion);
    setShowSuggestions(false);
    handleNaturalLanguageSearch(suggestion);
  };

  const handleKeyPress = (e) => {
    if (e?.key === 'Enter' && !isProcessing) {
      handleNaturalLanguageSearch();
    }
  };

  const toggleSearchMode = () => {
    setUseNaturalLanguage(!useNaturalLanguage);
    setNaturalLanguageQuery('');
    setError(null);
    setShowSuggestions(false);
    setSuggestions([]);
  };

  return (
    <div className="space-y-4">
      {/* Bulk Actions */}
      {selectedCount > 0 && (
        <BulkActions
          selectedCount={selectedCount}
          onMessage={onBulkMessage}
          onReveal={onBulkReveal}
          onArchive={onBulkArchive}
          onClearSelection={onClearSelection}
        />
      )}
      {/* Main Filter Bar */}
      <div className="bg-white border border-gray-200 rounded-lg p-3 shadow-sm">
        {/* Search Mode Toggle */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Icon
              name={useNaturalLanguage ? "Sparkles" : "Search"}
              size={16}
              color="#3B82F6"
            />
            <span className="text-sm font-medium text-gray-700">
              {useNaturalLanguage ? 'AI-Powered Search' : 'Manual Filters'}
            </span>
          </div>
          <button
            onClick={toggleSearchMode}
            className="text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
          >
            <Icon
              name="RefreshCw"
              size={12}
              color="#3B82F6"
            />
            Switch to {useNaturalLanguage ? 'Manual' : 'AI'}
          </button>
        </div>

        {useNaturalLanguage ? (
          // Natural Language Search
          (<div className="space-y-2">
            <div className="relative">
              <Icon
                name="Sparkles"
                size={16}
                color="#6B7280"
                className="absolute left-2.5 top-1/2 -translate-y-1/2"
              />
              <input
                type="text"
                placeholder='Try "show me technology roles" or "meetings scheduled this week"...'
                value={naturalLanguageQuery}
                onChange={(e) => setNaturalLanguageQuery(e?.target?.value)}
                onKeyPress={handleKeyPress}
                onFocus={() => suggestions?.length > 0 && setShowSuggestions(true)}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                disabled={isProcessing}
                className="w-full bg-white border border-gray-300 rounded-lg pl-9 pr-24 py-2 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:cursor-not-allowed"
              />
              <button
                onClick={() => handleNaturalLanguageSearch()}
                disabled={isProcessing || !naturalLanguageQuery?.trim()}
                className="absolute right-2 top-1/2 -translate-y-1/2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white px-3 py-1 rounded text-xs font-medium flex items-center gap-1 transition-colors"
              >
                {isProcessing ? (
                  <>
                    <Icon name="Loader2" size={12} className="animate-spin" />
                    Searching...
                  </>
                ) : (
                  <>
                    <Icon name="Search" size={12} />
                    Search
                  </>
                )}
              </button>

              {/* Suggestions Dropdown */}
              {showSuggestions && suggestions?.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  <div className="p-2">
                    <div className="text-xs font-medium text-gray-500 mb-2 px-2">Suggestions</div>
                    {suggestions?.map((suggestion, index) => (
                      <button
                        key={index}
                        onClick={() => handleSuggestionClick(suggestion)}
                        className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-blue-50 rounded flex items-center gap-2 transition-colors"
                      >
                        <Icon name="Lightbulb" size={14} color="#F59E0B" />
                        {suggestion}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            {/* Error Message */}
            {error && (
              <div className="flex items-start gap-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
                <Icon name="AlertCircle" size={14} color="#DC2626" className="flex-shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}
            {/* Examples */}
            <div className="flex flex-wrap gap-2">
              <span className="text-xs text-gray-500">Try:</span>
              {['show me technology roles', 'meetings scheduled', 'pending reveals', 'most active conversations']?.map((example) => (
                <button
                  key={example}
                  onClick={() => {
                    setNaturalLanguageQuery(example);
                    handleNaturalLanguageSearch(example);
                  }}
                  disabled={isProcessing}
                  className="text-xs text-blue-600 hover:text-blue-700 hover:underline disabled:text-gray-400 disabled:no-underline"
                >
                  "{example}"
                </button>
              ))}
            </div>
          </div>)
        ) : (
          // Manual Filters
          (<div className="flex items-center gap-3">
            {/* Search */}
            <div className="flex-1 relative">
              <Icon
                name="Search"
                size={16}
                color="#6B7280"
                className="absolute left-2.5 top-1/2 -translate-y-1/2"
              />
              <input
                type="text"
                placeholder="Search matches..."
                value={filters?.search}
                onChange={(e) => onFilterChange('search', e?.target?.value)}
                className="w-full bg-white border border-gray-300 rounded-lg pl-9 pr-3 py-1.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            {/* Stage Filter */}
            <select
              value={filters?.stage}
              onChange={(e) => onFilterChange('stage', e?.target?.value)}
              className="bg-white border border-gray-300 rounded-lg px-3 py-1.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">Filter by Status</option>
              <option value="initial">Initial Connection</option>
              <option value="conversation">In Conversation</option>
              <option value="reveal-pending">Reveal Pending</option>
              <option value="reveal-approved">Reveal Approved</option>
              <option value="meeting">Meeting Scheduled</option>
            </select>
          </div>)
        )}
      </div>
      {/* Saved Filters & Search History */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <SavedFilters
          filters={savedFilters}
          onApply={onApplyFilter}
          onDelete={onDeleteFilter}
          onSave={onSaveFilter}
          currentFilters={filters}
        />
        <SearchHistory
          searches={searchHistory}
          onApply={onSearch}
          onClear={onClearHistory}
        />
      </div>
    </div>
  );
};

export default FilterBar;